import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Avatar,
  Paper,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  PieChart as PieChartIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  Bookmark as BookmarkIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSettingsAccounts } from '../../store/settingsSlice';
import { fetchInstagramAnalytics } from '../../Action/socialMediaAPI';

// Import Chart.js components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const InstagramAnalytics = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const accounts = useSelector((state) => state.settings.accounts || []);
  const accountsLoading = useSelector((state) => state.settings.status === 'loading');
  
  console.log('All accounts loaded:', accounts);
  
  // Filter Instagram accounts
  const instagramAccounts = useMemo(() => {
    const filtered = accounts.filter(acc => acc?.provider?.toLowerCase() === 'instagram' && acc?.status === 'Active');
    console.log('Filtered Instagram accounts:', filtered);
    return filtered;
  }, [accounts]);

  // Component state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageDialog, setImageDialog] = useState({ open: false, url: '' });

  // Load accounts on mount
  useEffect(() => {
    console.log('Component mounted, fetching accounts...');
    dispatch(fetchSettingsAccounts());
  }, [dispatch]);

  // Auto-select first Instagram account
  useEffect(() => {
    if (instagramAccounts.length > 0 && !selectedAccount) {
      const firstAccount = instagramAccounts[0];
      console.log('Auto-selecting first Instagram account:', firstAccount);
      setSelectedAccount(firstAccount);
    }
  }, [instagramAccounts, selectedAccount]);

  // Fetch analytics when account changes
  const fetchAnalytics = useCallback(async () => {
    if (!selectedAccount) {
      console.log('No account selected, skipping fetch');
      return;
    }

    // Use username field as pageId for Instagram
    const pageId = selectedAccount.username;
    const accessToken = selectedAccount.pageToken;

    console.log('Fetching analytics for Instagram account:', {
      accountId: selectedAccount.accountId,
      username: pageId,
      hasToken: !!accessToken,
    });

    if (!pageId || !accessToken) {
      setError('Instagram account configuration is incomplete. Please reconnect your Instagram account in Settings to continue.');
      return;
    }

    // Clear old data before fetching new data
    setAnalyticsData(null);
    setLoading(true);
    setError(null);

    try {
      const result = await fetchInstagramAnalytics(pageId, accessToken);
      console.log('Analytics result:', result);
      
      if (result.success) {
        setAnalyticsData(result.data);
        setError(null);
      } else {
        // Provide user-friendly error messages based on the error type
        let friendlyMessage = result.message || 'Failed to load Instagram analytics';
        
        if (result.message?.includes('HTTP error! status: 500')) {
          friendlyMessage = 'Instagram server is temporarily unavailable. Please try again in a few moments.';
        } else if (result.message?.includes('HTTP error! status: 401') || result.message?.includes('401')) {
          friendlyMessage = 'Your Instagram access token has expired. Please reconnect your Instagram account in Settings.';
        } else if (result.message?.includes('HTTP error! status: 403') || result.message?.includes('403')) {
          friendlyMessage = 'Access denied. Please check your Instagram account permissions and reconnect if needed.';
        } else if (result.message?.includes('HTTP error! status: 429') || result.message?.includes('429')) {
          friendlyMessage = 'Instagram API rate limit reached. Please wait a few minutes and try again.';
        } else if (result.message?.includes('Network error') || result.message?.includes('fetch')) {
          friendlyMessage = 'Network connection error. Please check your internet connection and try again.';
        }
        
        setError(friendlyMessage);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      
      // Provide user-friendly error messages for exceptions
      let friendlyMessage = 'An unexpected error occurred while loading your Instagram analytics.';
      
      if (err.message?.includes('fetch') || err.message?.includes('Network')) {
        friendlyMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.message) {
        friendlyMessage = `Error: ${err.message}`;
      }
      
      setError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      fetchAnalytics();
    }
  }, [selectedAccount, fetchAnalytics]);

  const handleAccountChange = (event) => {
    const accountId = event.target.value;
    const account = instagramAccounts.find(acc => acc.accountId === accountId);
    console.log('Account changed:', account);
    setSelectedAccount(account);
  };

  const handleRefresh = () => {
    console.log('Refresh button clicked');
    fetchAnalytics();
  };

  const handleImageClick = (url) => {
    setImageDialog({ open: true, url });
  };

  const handleImageDialogClose = () => {
    setImageDialog({ open: false, url: '' });
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) return null;

    const profileData = analyticsData[0];
    const media = profileData?.media?.data || [];

    const totalReach = media.reduce((sum, post) => {
      const reach = post?.insights?.data?.find(i => i.name === 'reach')?.values?.[0]?.value || 0;
      return sum + reach;
    }, 0);

    const totalInteractions = media.reduce((sum, post) => {
      const interactions = post?.insights?.data?.find(i => i.name === 'total_interactions')?.values?.[0]?.value || 0;
      return sum + interactions;
    }, 0);

    const totalSaves = media.reduce((sum, post) => {
      const saves = post?.insights?.data?.find(i => i.name === 'saved')?.values?.[0]?.value || 0;
      return sum + saves;
    }, 0);

    const totalLikes = media.reduce((sum, post) => sum + (post?.like_count || 0), 0);
    const totalComments = media.reduce((sum, post) => sum + (post?.comments_count || 0), 0);

    return {
      followers: profileData?.followers_count || 0,
      mediaCount: profileData?.media_count || 0,
      totalReach,
      totalInteractions,
      totalSaves,
      totalLikes,
      totalComments,
      username: profileData?.username || '',
      biography: profileData?.biography || '',
      profilePicture: profileData?.profile_picture_url || '',
    };
  }, [analyticsData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!analyticsData || analyticsData.length === 0) return null;

    const media = analyticsData[0]?.media?.data || [];
    
    // Take last 10 posts for trend
    const recentPosts = media.slice(0, 10).reverse();

    // Engagement trend data (last 10 posts)
    const engagementTrend = {
      labels: recentPosts.map((_, idx) => `Post ${idx + 1}`),
      datasets: [
        {
          label: 'Likes',
          data: recentPosts.map(post => post?.like_count || 0),
          borderColor: 'rgba(225, 48, 108, 1)',
          backgroundColor: 'rgba(225, 48, 108, 0.2)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgba(225, 48, 108, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Comments',
          data: recentPosts.map(post => post?.comments_count || 0),
          borderColor: 'rgba(64, 93, 230, 1)',
          backgroundColor: 'rgba(64, 93, 230, 0.2)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgba(64, 93, 230, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Reach',
          data: recentPosts.map(post => {
            const reach = post?.insights?.data?.find(i => i.name === 'reach')?.values?.[0]?.value || 0;
            return reach;
          }),
          borderColor: 'rgba(253, 121, 168, 1)',
          backgroundColor: 'rgba(253, 121, 168, 0.2)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgba(253, 121, 168, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.4,
        },
      ],
    };

    // Metrics comparison
    const metricsComparison = {
      labels: ['Likes', 'Comments', 'Reach', 'Saves'],
      datasets: [
        {
          label: 'Total Metrics',
          data: [
            stats?.totalLikes || 0,
            stats?.totalComments || 0,
            stats?.totalReach || 0,
            stats?.totalSaves || 0,
          ],
          backgroundColor: [
            'rgba(225, 48, 108, 0.85)',
            'rgba(64, 93, 230, 0.85)',
            'rgba(253, 121, 168, 0.85)',
            'rgba(131, 58, 180, 0.85)',
          ],
          borderColor: [
            'rgba(225, 48, 108, 1)',
            'rgba(64, 93, 230, 1)',
            'rgba(253, 121, 168, 1)',
            'rgba(131, 58, 180, 1)',
          ],
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };

    // Media type distribution
    const imageCount = media.filter(post => post?.media_type === 'IMAGE').length;
    const videoCount = media.filter(post => post?.media_type === 'VIDEO').length;
    const carouselCount = media.filter(post => post?.media_type === 'CAROUSEL_ALBUM').length;

    const mediaTypeDistribution = {
      labels: ['Images', 'Videos', 'Carousels'],
      datasets: [
        {
          label: 'Media Type',
          data: [imageCount, videoCount, carouselCount],
          backgroundColor: [
            'rgba(225, 48, 108, 0.85)',
            'rgba(64, 93, 230, 0.85)',
            'rgba(253, 121, 168, 0.85)',
          ],
          borderColor: [
            'rgba(225, 48, 108, 1)',
            'rgba(64, 93, 230, 1)',
            'rgba(253, 121, 168, 1)',
          ],
          borderWidth: 3,
          hoverOffset: 15,
          hoverBorderWidth: 4,
        },
      ],
    };

    // Top 5 posts by likes
    const topPosts = media
      .slice()
      .sort((a, b) => (b?.like_count || 0) - (a?.like_count || 0))
      .slice(0, 5);

    const topPostsChart = {
      labels: topPosts.map((post, idx) => `Post ${idx + 1}`),
      datasets: [
        {
          label: 'Likes',
          data: topPosts.map(post => post?.like_count || 0),
          backgroundColor: 'rgba(131, 58, 180, 0.85)',
          borderColor: 'rgba(131, 58, 180, 1)',
          borderWidth: 2,
          borderRadius: 8,
        },
      ],
    };

    return {
      engagementTrend,
      metricsComparison,
      mediaTypeDistribution,
      topPostsChart,
    };
  }, [analyticsData, stats]);

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  // Loading state
  if (accountsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // No accounts state
  if (instagramAccounts.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">
          No Instagram accounts found. Please add an Instagram account in Settings to view analytics.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header with account selector */}
      <Box mb={4}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  background: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
                  width: 56,
                  height: 56,
                }}
              >
                <ImageIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ 
                  background: 'linear-gradient(135deg, #E1306C 0%, #833AB4 50%, #405DE6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Instagram Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your Instagram performance and engagement
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl 
              fullWidth 
              variant="outlined" 
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(225, 48, 108, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(225, 48, 108, 0.15)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E1306C',
                    },
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 16px rgba(225, 48, 108, 0.2)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E1306C',
                      borderWidth: 2,
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                  '&.Mui-focused': {
                    background: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  },
                },
              }}
            >
              <InputLabel>Select Account</InputLabel>
              <Select
                value={selectedAccount?.accountId || ''}
                onChange={handleAccountChange}
                label="Select Account"
              >
                {instagramAccounts.map((account) => (
                  <MenuItem 
                    key={account.accountId} 
                    value={account.accountId}
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(90deg, rgba(225, 48, 108, 0.08) 0%, rgba(131, 58, 180, 0.08) 100%)',
                      },
                      '&.Mui-selected': {
                        background: 'linear-gradient(90deg, rgba(225, 48, 108, 0.12) 0%, rgba(131, 58, 180, 0.12) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, rgba(225, 48, 108, 0.16) 0%, rgba(131, 58, 180, 0.16) 100%)',
                        },
                      },
                    }}
                  >
                    <ImageIcon sx={{ 
                      fontSize: 20,
                      background: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }} />
                    <span>{account.accountName || account.username || `Account ${account.accountId}`}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }} />}
              onClick={handleRefresh}
              disabled={loading || !selectedAccount}
              fullWidth
              size="medium"
              sx={{
                background: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
                color: '#fff',
                fontWeight: 'bold',
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(225, 48, 108, 0.3)',
                textTransform: 'none',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #C13584 0%, #7232A8 100%)',
                  boxShadow: '0 6px 20px rgba(225, 48, 108, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 2px 8px rgba(225, 48, 108, 0.3)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                  color: '#9e9e9e',
                  boxShadow: 'none',
                },
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Card 
          sx={{ 
            mb: 4, 
            borderRadius: 3, 
            boxShadow: 3,
            border: '2px solid #ef4444',
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: '#ef4444', mr: 2, width: 56, height: 56 }}>
                <CloseIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#991b1b">
                  Unable to Load Analytics
                </Typography>
                <Typography variant="body2" color="#7f1d1d">
                  We encountered an issue while fetching your Instagram data
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2, borderColor: '#fca5a5' }} />
            <Typography variant="body2" color="#7f1d1d" mb={2}>
              <strong>Error Details:</strong> {error}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={!selectedAccount}
                sx={{
                  bgcolor: '#ef4444',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#dc2626',
                  },
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={() => setError(null)}
                sx={{
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    borderColor: '#dc2626',
                    bgcolor: 'rgba(239, 68, 68, 0.04)',
                  },
                }}
              >
                Dismiss
              </Button>
            </Box>
            <Box mt={3} p={2} sx={{ bgcolor: 'rgba(255, 255, 255, 0.7)', borderRadius: 2 }}>
              <Typography variant="caption" color="#7f1d1d" fontWeight="600" display="block" mb={1}>
                💡 Troubleshooting Tips:
              </Typography>
              <Typography variant="caption" color="#7f1d1d" display="block">
                • Check if your Instagram access token is valid and not expired<br/>
                • Ensure the selected account has proper permissions<br/>
                • Verify your internet connection is stable<br/>
                • Try refreshing the page or selecting a different account
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      )}

      {!loading && stats && analyticsData && (
        <>
          {/* Profile Info */}
          {stats.profilePicture && (
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <Avatar
                      src={stats.profilePicture}
                      alt={stats.username}
                      sx={{ width: 80, height: 80, border: '3px solid #E1306C' }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      @{stats.username}
                    </Typography>
                    {stats.biography && (
                      <Typography variant="body2" color="text.secondary">
                        {stats.biography}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Statistics Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.followers.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Followers
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <EmojiEmotionsIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: '#fff',
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.mediaCount.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Posts
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <ImageIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: '#fff',
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.totalReach.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Reach
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <VisibilityIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3, 
                boxShadow: 3,
                background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
                color: '#fff',
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {stats.totalInteractions.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Interactions
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <TrendingUpIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Section */}
          {chartData && (
            <Box mb={4}>
              <Box 
                mb={3}
                p={2}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  boxShadow: 3,
                }}
              >
                <Typography variant="h5" fontWeight="bold" color="#fff">
                  📊 Analytics Visualizations
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Engagement Trend */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(to bottom, #ffffff 0%, #f8faff 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: '#405DE6', mr: 2 }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Engagement Trend
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last 10 posts performance
                        </Typography>
                      </Box>
                    </Box>
                    <Box height={300}>
                      <Line data={chartData.engagementTrend} options={lineChartOptions} />
                    </Box>
                  </Paper>
                </Grid>

                {/* Metrics Comparison */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(to bottom, #ffffff 0%, #fff5f5 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: '#E1306C', mr: 2 }}>
                        <BarChartIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Metrics Comparison
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Overall engagement breakdown
                        </Typography>
                      </Box>
                    </Box>
                    <Box height={300}>
                      <Bar data={chartData.metricsComparison} options={barChartOptions} />
                    </Box>
                  </Paper>
                </Grid>

                {/* Media Type Distribution */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(to bottom, #ffffff 0%, #fffbf5 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: '#FD79A8', mr: 2 }}>
                        <PieChartIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Media Type Distribution
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Content format breakdown
                        </Typography>
                      </Box>
                    </Box>
                    <Box height={300}>
                      <Doughnut data={chartData.mediaTypeDistribution} options={doughnutOptions} />
                    </Box>
                  </Paper>
                </Grid>

                {/* Top Posts */}
                <Grid item xs={12} lg={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(to bottom, #ffffff 0%, #fcf5ff 100%)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: '#833AB4', mr: 2 }}>
                        <EmojiEmotionsIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Top 5 Posts by Likes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Most liked content
                        </Typography>
                      </Box>
                    </Box>
                    <Box height={300}>
                      <Bar data={chartData.topPostsChart} options={horizontalBarOptions} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Posts List */}
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Recent Posts
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {analyticsData[0]?.media?.data && analyticsData[0].media.data.length > 0 ? (
                <Grid container spacing={3}>
                  {analyticsData[0].media.data.map((post) => {
                    const reach = post?.insights?.data?.find(i => i.name === 'reach')?.values?.[0]?.value || 0;
                    const saves = post?.insights?.data?.find(i => i.name === 'saved')?.values?.[0]?.value || 0;

                    return (
                      <Grid item xs={12} key={post.id}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Grid container spacing={2}>
                              {/* Media Thumbnail */}
                              <Grid item xs={12} sm={3}>
                                <Box
                                  sx={{
                                    position: 'relative',
                                    paddingTop: '100%',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 0.8,
                                    },
                                  }}
                                  onClick={() => handleImageClick(post.media_url)}
                                >
                                  {post.media_type === 'VIDEO' ? (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#000',
                                      }}
                                    >
                                      <video
                                        src={post.media_url}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }}
                                      />
                                      <VideoIcon
                                        sx={{
                                          position: 'absolute',
                                          fontSize: 48,
                                          color: '#fff',
                                          opacity: 0.8,
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Box
                                      component="img"
                                      src={post.media_url}
                                      alt="Post"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                  )}
                                  <Chip
                                    label={post.media_type}
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      bgcolor: 'rgba(0,0,0,0.7)',
                                      color: '#fff',
                                    }}
                                  />
                                </Box>
                              </Grid>

                              {/* Post Details */}
                              <Grid item xs={12} sm={9}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {new Date(post.timestamp).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </Typography>

                                <Typography
                                  variant="body1"
                                  sx={{
                                    mb: 2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {post.caption || 'No caption'}
                                </Typography>

                                {/* Engagement Metrics */}
                                <Grid container spacing={2}>
                                  <Grid item>
                                    <Box display="flex" alignItems="center">
                                      <FavoriteIcon sx={{ color: '#E1306C', mr: 0.5, fontSize: 20 }} />
                                      <Typography variant="body2" fontWeight="bold">
                                        {post.like_count?.toLocaleString() || 0}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" ml={0.5}>
                                        Likes
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid item>
                                    <Box display="flex" alignItems="center">
                                      <CommentIcon sx={{ color: '#405DE6', mr: 0.5, fontSize: 20 }} />
                                      <Typography variant="body2" fontWeight="bold">
                                        {post.comments_count?.toLocaleString() || 0}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" ml={0.5}>
                                        Comments
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid item>
                                    <Box display="flex" alignItems="center">
                                      <VisibilityIcon sx={{ color: '#FD79A8', mr: 0.5, fontSize: 20 }} />
                                      <Typography variant="body2" fontWeight="bold">
                                        {reach.toLocaleString()}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" ml={0.5}>
                                        Reach
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid item>
                                    <Box display="flex" alignItems="center">
                                      <BookmarkIcon sx={{ color: '#833AB4', mr: 0.5, fontSize: 20 }} />
                                      <Typography variant="body2" fontWeight="bold">
                                        {saves.toLocaleString()}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" ml={0.5}>
                                        Saves
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Alert severity="info">No posts available</Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!loading && !analyticsData && !error && selectedAccount && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            Click "Refresh" to load analytics data
          </Typography>
        </Box>
      )}

      {/* Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={handleImageDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ position: 'relative', p: 0 }}>
          <IconButton
            onClick={handleImageDialogClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={imageDialog.url}
            alt="Post"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InstagramAnalytics;
